import React, { useState, useEffect } from 'react';
import { phoneNumbersAPI, smsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, MessageSquare, Phone } from 'lucide-react';
import { toast } from 'sonner';

export function CommunicationsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Missed Call settings
  const [missedCall, setMissedCall] = useState({
    enabled: false,
    cooldown_minutes: 60,
    template_id: null,
  });

  // OTW settings
  const [otw, setOtw] = useState({
    enabled: false,
    template_id: null,
    default_eta_minutes: null,
  });

  useEffect(() => {
    Promise.all([
      phoneNumbersAPI.getMissedCallSettings().catch(() => ({ data: { enabled: false, cooldown_minutes: 60, template_id: null } })),
      phoneNumbersAPI.getOtwSettings().catch(() => ({ data: { enabled: false, template_id: null, default_eta_minutes: null } })),
      smsAPI.templates.list().catch(() => ({ data: [] })),
    ]).then(([mcRes, otwRes, tplRes]) => {
      setMissedCall(mcRes.data);
      setOtw(otwRes.data);
      setTemplates(tplRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const saveMissedCall = async () => {
    setSaving(true);
    try {
      const res = await phoneNumbersAPI.updateMissedCallSettings(missedCall);
      setMissedCall(res.data);
      toast.success('Missed call auto-text settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveOtw = async () => {
    setSaving(true);
    try {
      const res = await phoneNumbersAPI.updateOtwSettings(otw);
      setOtw(res.data);
      toast.success('On-the-way auto-text settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const otwTemplates = templates.filter(t => t.category === 'technician_on_the_way' || t.category === 'general');
  const missedCallTemplates = templates.filter(t => t.category === 'missed_call_followup' || t.category === 'general');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Missed Call Auto-Text */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            <div>
              <CardTitle className="text-base">Missed Call Auto-Text</CardTitle>
              <CardDescription>Automatically send an SMS when an inbound call goes unanswered.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between" data-testid="missed-call-autotext-toggle">
            <Label htmlFor="mc-enabled">Enable missed call auto-text</Label>
            <Switch
              id="mc-enabled"
              checked={missedCall.enabled}
              onCheckedChange={(v) => setMissedCall(prev => ({ ...prev, enabled: v }))}
            />
          </div>
          {missedCall.enabled && (
            <>
              <div className="space-y-1.5">
                <Label>Cooldown (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  max={1440}
                  value={missedCall.cooldown_minutes}
                  onChange={(e) => setMissedCall(prev => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 60 }))}
                  className="w-32"
                  data-testid="mc-cooldown-input"
                />
                <p className="text-xs text-muted-foreground">Minimum time between auto-texts to the same number.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Template</Label>
                <Select
                  value={missedCall.template_id || 'default'}
                  onValueChange={(v) => setMissedCall(prev => ({ ...prev, template_id: v === 'default' ? null : v }))}
                >
                  <SelectTrigger data-testid="mc-template-select"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default text</SelectItem>
                    {missedCallTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <Button onClick={saveMissedCall} disabled={saving} size="sm" data-testid="save-missed-call-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Save
          </Button>
        </CardContent>
      </Card>

      {/* On-The-Way Auto-Text */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            <div>
              <CardTitle className="text-base">Technician On-The-Way Text</CardTitle>
              <CardDescription>Automatically text the customer when a dispatch event status changes to &quot;En Route&quot;. Can also be sent manually from the dispatch board.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between" data-testid="otw-autotext-toggle">
            <Label htmlFor="otw-enabled">Enable automatic on-the-way text</Label>
            <Switch
              id="otw-enabled"
              checked={otw.enabled}
              onCheckedChange={(v) => setOtw(prev => ({ ...prev, enabled: v }))}
            />
          </div>
          {otw.enabled && (
            <>
              <div className="space-y-1.5">
                <Label>Template</Label>
                <Select
                  value={otw.template_id || 'default'}
                  onValueChange={(v) => setOtw(prev => ({ ...prev, template_id: v === 'default' ? null : v }))}
                >
                  <SelectTrigger data-testid="otw-template-select"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default text</SelectItem>
                    {otwTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Variables: {'{{customer_first_name}}'}, {'{{tech_name}}'}, {'{{eta}}'}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Default ETA (minutes from now)</Label>
                <Input
                  type="number"
                  min={1}
                  max={480}
                  value={otw.default_eta_minutes || ''}
                  onChange={(e) => setOtw(prev => ({ ...prev, default_eta_minutes: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="Leave blank if no default ETA"
                  className="w-48"
                  data-testid="otw-eta-input"
                />
                <p className="text-xs text-muted-foreground">Fallback ETA if none is provided. Leave blank to omit ETA from the message.</p>
              </div>
            </>
          )}
          <Button onClick={saveOtw} disabled={saving} size="sm" data-testid="save-otw-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Save
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
